import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ShieldCheck, Eye, UserCog, GraduationCap, ChevronRight } from "lucide-react";

import { api, formatApiError, TOKEN_KEY } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROLE_ICON = {
    ORG_ADMIN: ShieldCheck,
    SME_REVIEWER: Eye,
    TRAINER: UserCog,
    LEARNER: GraduationCap,
};

const FALLBACK_ROLES = [
    { role: "ORG_ADMIN",    label: "Enter as Administrator", description: "Full access — upload SOPs, approve storyboards, launch builds." },
    { role: "SME_REVIEWER", label: "Enter as Reviewer",      description: "Review and sign off on storyboards and final cuts." },
    { role: "TRAINER",      label: "Enter as Trainer",       description: "Edit scenes, swap visuals, regenerate assets." },
    { role: "LEARNER",      label: "Enter as Learner",       description: "Watch assigned training films and take quizzes." },
];

export default function Login() {
    const { status, login, refresh, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState("");
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [roles, setRoles] = useState(FALLBACK_ROLES);
    const [demoMode, setDemoMode] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const { data } = await api.get("/auth/available-roles");
                if (!active) return;
                setDemoMode(!!data.demo_mode);
                if (Array.isArray(data.roles) && data.roles.length) {
                    setRoles(data.roles);
                }
            } catch (_e) {
                // Endpoint missing or server down → keep fallback roles.
            }
        })();
        return () => { active = false; };
    }, []);

    if (status === "authed") {
        return <Navigate to={user?.role === "LEARNER" ? "/learn" : "/"} replace />;
    }

    const quickLogin = async (role) => {
        setError(""); setBusy(role);
        try {
            const { data } = await api.post("/auth/quick-login", { role });
            localStorage.setItem(TOKEN_KEY, data.access_token);
            await refresh();
        } catch (e) {
            // Graceful fallback if the endpoint 404s on a stale deploy.
            const msg = formatApiError(e);
            setError(msg || "Couldn't sign in with that role. Try the email form below.");
            setShowEmailForm(true);
        } finally {
            setBusy("");
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setBusy("email");
        const res = await login(email.trim(), password);
        setBusy("");
        if (!res.ok) setError(res.error);
    };

    return (
        <div
            data-testid="login-page"
            className="flex min-h-screen"
            style={{ fontFamily: "Inter, system-ui, sans-serif" }}
        >
            {/* ---------- LEFT PANEL (brand) ---------- */}
            <div
                className="hidden flex-col justify-between p-14 lg:flex lg:w-5/12"
                style={{
                    background: "linear-gradient(180deg,#0B1F3A 0%,#0A1428 100%)",
                    color: "#F5F2EC",
                }}
            >
                <div>
                    <div className="text-sm font-semibold tracking-tight">ACME-SIPL</div>
                    <div className="mt-0.5 text-[11px]" style={{ color: "#A8B2C1" }}>
                        Powered by Sanghvi Infotech Pvt Ltd
                    </div>
                </div>

                <div className="space-y-5 max-w-md">
                    <div
                        className="h-[3px] w-14"
                        style={{ background: "#D4A574" }}
                    />
                    <h1
                        className="text-5xl leading-[1.08] tracking-tight"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600 }}
                    >
                        Approved work instructions,<br />
                        <span style={{ color: "#A8B2C1" }}>rendered as training film.</span>
                    </h1>
                    <p className="text-sm leading-relaxed" style={{ color: "#A8B2C1" }}>
                        An enterprise platform for turning verified operating
                        procedures into cinematic, trainer-led learning modules,
                        governed by your standards.
                    </p>
                </div>

                <div
                    className="text-[11px] uppercase tracking-[0.22em]"
                    style={{ color: "#4C5A72" }}
                >
                    Cycle 1 · Phase 1A.1 · Foundations
                </div>
            </div>

            {/* ---------- RIGHT PANEL (role picker) ---------- */}
            <div className="flex w-full flex-col justify-center bg-white px-6 py-12 lg:w-7/12 lg:px-20">
                <div className="mx-auto w-full max-w-md">
                    <div
                        className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                        style={{ color: "#D4A574" }}
                    >
                        Sign in
                    </div>
                    <h2
                        className="mt-2 text-3xl tracking-tight text-neutral-900"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 600 }}
                    >
                        Who are you today?
                    </h2>
                    <p className="mt-2 text-sm text-neutral-500">
                        Pick a role to jump straight in. One click, no credentials.
                    </p>

                    {/* Role buttons */}
                    {demoMode && (
                        <div
                            data-testid="role-buttons"
                            className="mt-8 space-y-3"
                        >
                            {roles.map((r) => {
                                const Icon = ROLE_ICON[r.role] || ShieldCheck;
                                const loading = busy === r.role;
                                return (
                                    <button
                                        key={r.role}
                                        type="button"
                                        disabled={!!busy}
                                        data-testid={`role-btn-${r.role}`}
                                        onClick={() => quickLogin(r.role)}
                                        className="group flex w-full items-center justify-between gap-4 rounded-md border border-neutral-200 bg-white px-5 py-4 text-left transition-all hover:border-neutral-900 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <div className="flex items-start gap-4">
                                            <span
                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
                                                style={{
                                                    background: "#fdfaf4",
                                                    color: "#D4A574",
                                                }}
                                            >
                                                <Icon size={18} />
                                            </span>
                                            <div>
                                                <div className="text-sm font-medium text-neutral-900">
                                                    {loading ? "Signing in…" : r.label}
                                                </div>
                                                <div className="mt-0.5 text-xs text-neutral-500">
                                                    {r.description}
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight
                                            size={16}
                                            className="shrink-0 text-neutral-300 transition-colors group-hover:text-neutral-900"
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {error && (
                        <div
                            data-testid="login-error"
                            className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                        >
                            {error}
                        </div>
                    )}

                    {/* Email form — hidden by default behind a link */}
                    {!showEmailForm && (
                        <div className="mt-8 border-t border-neutral-100 pt-5 text-center">
                            <button
                                type="button"
                                data-testid="show-email-form"
                                className="text-xs text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline"
                                onClick={() => setShowEmailForm(true)}
                            >
                                Sign in with email instead
                            </button>
                        </div>
                    )}

                    {showEmailForm && (
                        <form
                            onSubmit={submit}
                            className="mt-8 space-y-4 border-t border-neutral-100 pt-6"
                            data-testid="login-form"
                        >
                            <div className="space-y-1.5">
                                <Label htmlFor="email" className="text-xs font-medium text-neutral-700">
                                    Email
                                </Label>
                                <Input
                                    id="email"
                                    data-testid="login-email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="h-10"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="password" className="text-xs font-medium text-neutral-700">
                                    Password
                                </Label>
                                <Input
                                    id="password"
                                    data-testid="login-password"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="h-10"
                                />
                            </div>
                            <Button
                                type="submit"
                                data-testid="login-submit"
                                disabled={!!busy}
                                className="h-10 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                            >
                                {busy === "email" ? "Signing in…" : "Sign in"}
                            </Button>
                        </form>
                    )}

                    <p className="mt-8 text-[11px] text-neutral-400">
                        Password reset is available from the next release.
                    </p>
                </div>
            </div>
        </div>
    );
}
